// @ts-nocheck
// Agent Portal (/cti2) — screen derivation + its two side effects, split
// out of AgentPortalPage.tsx: the incoming-call ringtone, and the
// caller-contact lookup CustomerProfileCard reads (production populates
// that via RingingScreen.tsx/OnCallScreen.tsx's own lookup effects; /cti2
// doesn't render either of those, so this covers both the ringing and
// oncall cases those used to handle separately).
import { useEffect, useMemo, useRef } from 'react';
import { useCallStore } from '@/router/zustand/callStore';
import { getContactGroupMemberByNumber } from '@/service/contactGroupMember';
import ringtone from '@/assets/incoming_calls_ringtones/ringtone1.mp3';
import { isMobileProfileActive } from '@/utilities/mobileCallEvents';

export function useScreenState({ callState, manualScreen, outgoingRingingActive, customerRingingActive, declineIntent, callerNumber }) {
  const callerContact = useCallStore((s) => s.callerContact);
  const setCallerContact = useCallStore((s) => s.setCallerContact);
  const activeCallMeta = useCallStore((s) => s.activeCallMeta);
  const screen = useMemo(() => {
    if (declineIntent && (callState === 'incoming' || outgoingRingingActive)) return 'idle';
    const agentOnCall = ['oncall', 'connected', 'hold', 'conference'].includes(callState);
    if (callState === 'incoming' || outgoingRingingActive || (customerRingingActive && !agentOnCall)) return 'ringing';
    if (manualScreen) return manualScreen;
    switch (callState) {
      case 'oncall':
      case 'conference': return 'oncall';
      default: return 'idle';
    }
  }, [callState, manualScreen, outgoingRingingActive, customerRingingActive, declineIntent]);

  useEffect(() => {
    if ((screen !== 'oncall' && screen !== 'ringing') || callerContact) return undefined;
    const lookupNumber = String(activeCallMeta?.customer_number || callerNumber || '').replace(/\D/g, '').slice(-10);
    if (!lookupNumber) return undefined;
    let cancelled = false;
    (async () => {
      const data = await getContactGroupMemberByNumber(lookupNumber);
      if (!cancelled && data) setCallerContact(data);
    })();
    return () => { cancelled = true; };
  }, [screen, callerNumber, activeCallMeta?.customer_number, callerContact, setCallerContact]);

  const ringtoneRef = useRef(null);

  // Browsers block programmatic audio.play() until the page has had a
  // user gesture (click/touch) — a ringtone is never actually triggered
  // BY a gesture (it starts the instant a call rings in), so on a fresh
  // page load with no interaction yet it silently failed here every time
  // (the `.play().catch(() => {})` below swallows the NotAllowedError with
  // no fallback). Same one-time unlock AgentPortalPage.tsx already does
  // for the hangup sound — play + immediately pause once on the first
  // click/touch, so the element is "primed" before it's ever needed for
  // real.
  useEffect(() => {
    const unlock = () => {
      const audio = ringtoneRef.current;
      if (!audio) return;
      audio.play().then(() => { audio.pause(); audio.currentTime = 0; }).catch(() => {});
      document.removeEventListener('click', unlock);
      document.removeEventListener('touchstart', unlock);
    };
    document.addEventListener('click', unlock, { once: true });
    document.addEventListener('touchstart', unlock, { once: true });
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('touchstart', unlock);
    };
  }, []);

  const legRinging = useCallStore(
    (s) => s.outgoingCallStatus === 'ringing-agent' || s.outgoingCallStatus === 'ringing-customer'
  );

  useEffect(() => {
    if (legRinging && isMobileProfileActive()) {
      if (ringtoneRef.current) {
        ringtoneRef.current.loop = true;
        ringtoneRef.current.play().catch(() => {});
      }
      return () => {
        if (ringtoneRef.current) { ringtoneRef.current.pause(); ringtoneRef.current.currentTime = 0; }
      };
    }
    if (screen !== 'ringing' || callState !== 'incoming') {
      if (ringtoneRef.current) { ringtoneRef.current.pause(); ringtoneRef.current.currentTime = 0; }
      return undefined;
    }
    const answerMode = String(useCallStore.getState().sipHeaders?.['answer-mode'] || '').trim().toLowerCase();
    if (['true', '1', 'yes', 'y', 'auto'].includes(answerMode)) return undefined;
    if (ringtoneRef.current) {
      ringtoneRef.current.loop = true;
      ringtoneRef.current.play().catch(() => {});
    }
    return () => {
      if (ringtoneRef.current) { ringtoneRef.current.pause(); ringtoneRef.current.currentTime = 0; }
    };
  }, [screen, callState, legRinging]);

  return { screen, ringtoneRef, ringtoneSrc: ringtone };
}
